import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addTeamMember,
  createTeam,
  issueMyGrant,
  linkDevelopers,
  listMyTeams,
  listTeams,
  removeTeamMember,
  revokeDeveloper,
} from "@/api/code-companion-access";

const teamsKey = ["cc", "teams"] as const;
const devsKey = (p: string) => ["cc", "developers", p] as const;

export function useCcTeams(enabled = true) {
  return useQuery({ queryKey: teamsKey, queryFn: listTeams, enabled });
}

export function useCcMyTeams() {
  return useQuery({ queryKey: ["cc", "my-teams"], queryFn: listMyTeams });
}

export function useCcCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createTeam(name),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamsKey }),
  });
}

export function useCcAddTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: number; userId: string }) =>
      addTeamMember(teamId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamsKey }),
  });
}

export function useCcRemoveTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: number; userId: string }) =>
      removeTeamMember(teamId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamsKey }),
  });
}

export function useCcLinkDevelopers(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { user_id?: string; team_id?: number }) =>
      linkDevelopers(projectId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: devsKey(projectId) }),
  });
}

export function useCcRevokeDeveloper(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => revokeDeveloper(projectId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: devsKey(projectId) }),
  });
}

export function useCcIssueMyGrant(projectId: string) {
  return useMutation({
    mutationFn: () => issueMyGrant(projectId),
  });
}
